import React, { useEffect } from "react";
import { RouteComponentProps, withRouter } from "react-router-dom";

import BrandPerformanceTrendTile from "../BrandPerformanceTrendTile";
import BrandPerformanceTableTile from "../BrandPerformanceTableTile";
import { useQuery } from "@tanstack/react-query";
import { getAwarenessAndConsiderationByModel, getPurchaseFunnelData } from "../../../../api/brandPerformanceApi";
import { RootStateOrAny, useSelector } from "react-redux";
import { isNullOrUndefined } from "../../../../utils/utilityFunctions";

interface Props extends RouteComponentProps {
  diff: number;
  granularity: string;
}

const TableTooltipParagraphs = () => (
  <>
    <p>
      YouGov has a customizable section to test ad hoc questions, which may vary depending on region. As shown here, two model-level questions
      are tested and are only available for Awareness and Consideration.
    </p>
  </>
);

// Temporarily hidden; set to true to restore the Awareness and Consideration by Model table
const SHOW_AWARENESS_CONSIDERATION_BY_MODEL = false;

const PurchaseFunnelContent = withRouter((props: Props) => {
  const { diff, history, granularity } = props;

  const params = useSelector((state: RootStateOrAny) => state.parameters);
  const dataSource = params?.data_source;

  const {
    data: purchaseFunnelTrendData,
    isFetching: loadingPurchaseFunnelTrendData,
    refetch: refetchPurchaseFunnelTrendData,
  } = useQuery(["purchaseFunnelTrendData", granularity, history.location.search], () => getPurchaseFunnelData(params), { enabled: false });

  const {
    data: awarenessAndConsiderationData,
    isFetching: loadingAwarenessAndConsideration,
    refetch: refetchAwarenessAndConsideration,
  } = useQuery(
    ["awarenessAndConsiderationByModel", history.location.search],
    () => getAwarenessAndConsiderationByModel(params),
    { enabled: false }
  );

  // when the url changes, refetch the data
  useEffect(() => {
    refetchPurchaseFunnelTrendData();
    if (SHOW_AWARENESS_CONSIDERATION_BY_MODEL) refetchAwarenessAndConsideration();
  },[history.location.search]);

  const columns = [
    {
      id: "model",
      Header: "Model",
      accessor: "model",
      defaultCanSort: true,
      sortDescFirst: true,
      sortType: "baseFirstAlphanumericIgnoreCase",
      Cell: (props1: { value: string }) => props1.value,
    },
    {
      id: "awareness",
      Header: "Awareness",
      accessor: "awareness",
      defaultCanSort: true,
      sortDescFirst: true,
      sortType: "baseFirstBasicCustom",
      Cell: (props1: { cell: { row: { original: any } } }) => {
        const { awareness, model } = props1.cell.row.original;
        if (isNullOrUndefined(awareness)) return "n/a";
        return model === "Base" ? `${awareness}` : `${awareness}%`;
      },
    },
    {
      id: "consideration",
      Header: "Consideration",
      accessor: "consideration",
      defaultCanSort: true,
      sortDescFirst: true,
      sortType: "baseFirstBasicCustom",
      Cell: (props1: { cell: { row: { original: any } } }) => {
        const { consideration, model } = props1.cell.row.original;
        if (isNullOrUndefined(consideration)) return "n/a";
        return model === "Base" ? `${consideration}` : `${consideration}%`;
      },
    },
  ];

  return (
    <div className="purchase-funnel-main-content">
      <BrandPerformanceTrendTile
        title={`${params.competitor} Purchase Funnel`}
        chartId="purchaseFunnelChart"
        chartData={purchaseFunnelTrendData}
        loadingTrend={loadingPurchaseFunnelTrendData}
        diff={diff}
        isPercentage={true}
        numberFormat="#.0"
        chartGranularity={granularity}
      />
      {SHOW_AWARENESS_CONSIDERATION_BY_MODEL && dataSource !== "Gfk" && (
        <BrandPerformanceTableTile
          title="Awareness and Consideration by Model"
          tableId="awareness_consideration_table"
          tableData={awarenessAndConsiderationData}
          columns={columns}
          loadingTable={loadingAwarenessAndConsideration}
          initialSortCol={{ id: "model", desc: false }}
          TableTooltipParagraphs={TableTooltipParagraphs}
          nestedInData={false}
        />
      )}
    </div>
  );
});

export default PurchaseFunnelContent;
